import _ from 'lodash'
import { ok, err, Result } from 'neverthrow'
import { FusionError, serializeFusionError } from '@fusion-advantage/backcompat'

// required for native sqlite use
import { Capacitor } from '@capacitor/core'
import { CapacitorSQLite, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite'

// required for web sqljs use
import initSqlJs, { Database } from 'sql.js'

const DB_NAME = 'fusion'

type DatabaseConnection = SQLiteDBConnection | Database

let db: DatabaseConnection | null = null

export const killFusionLdb = (): Result<void, void> => {
	db = null
	return ok()
}

// the way we are accessing this is always single-threaded,
// and transactions stay open between statements;
// use a single instance/connection regardless of platform.
// see comments in ldb.test.ts for more detail
export const initFusionLdb = async (): Promise<Result<void, FusionError>> => {
	if (db) {
		return ok()
	}
	try {
		if (Capacitor.isNativePlatform()) {
			const sqlite = new SQLiteConnection(CapacitorSQLite)
			const ret = await sqlite.checkConnectionsConsistency()
			const isConn = (await sqlite.isConnection(DB_NAME, false))
			if (ret.result && isConn.result) {
				db = await sqlite.retrieveConnection(DB_NAME, false)
			} else {
				db = await sqlite.createConnection(DB_NAME, false, "no-encryption", 1, false)
			}
			await db.open()
		} else if (typeof window === 'undefined') {
			// non-browser (jest)
			const sql = await initSqlJs()
			db = new sql.Database()
		} else {
			const sql = await initSqlJs({ locateFile: () => '/static/sql-wasm.wasm?' + import.meta.env.BUILDTIME })
			db = new sql.Database()
		}
		if(!db) {
			throw new Error("ldb open attempted but null")
		}
	} catch(ex) {
		return err(serializeFusionError('initFusionLdb', ex))
	}
	return ok()
}

export const placeholders = (columns: number): string => {
	const ph: string[] = []
	for (let i = 0; i < columns; i++) {
		ph.push('?')
	}
	return ph.join(',')
}

export const bulkPlaceholders = (columns: number, rows: number): string => {
	const ph: string[] = []
	const row = '(' + placeholders(columns) + ')'
	for (let i = 0; i < rows; i++) {
		ph.push(row)
	}
	return ph.join(',')
}

// run a statement with optional parameters
export const sqlRun = async (query: string, params?: any[]): Promise<Result<void, FusionError>> => {
	try {
		if (Capacitor.isNativePlatform()) {
			// https://github.com/capacitor-community/sqlite/blob/master/docs/API.md#run
			// Execute a Single Statement
			await (db as SQLiteDBConnection).run(query, params)
		} else {
			// https://sql.js.org/documentation/Database.html#%5B%22run%22%5D
			// Execute an SQL query, ignoring the rows it returns.
			(db as Database).run(query, params)
		}
	} catch(ex) {
		return err(serializeFusionError('sqlRun', ex))
	}
	return ok()
}

// run a select statement, returning the results
export const sqlQuery = async <T extends object>(query: string, params?: any[]): Promise<Result<T[], FusionError>> => {
	try {
		if (Capacitor.isNativePlatform()) {
			const result = await (db as SQLiteDBConnection).query(query, params)
			return ok(result.values as T[])
		} else {
			const stmt = (db as Database).prepare(query, params)
			const values: T[] = []
			while (stmt.step()) {
				values.push(stmt.getAsObject() as T)
			}
			stmt.free()
			return ok(values)
		}
	} catch(ex) {
		return err(serializeFusionError('sqlQuery', ex))
	}
}

// count rows in table; mainly for tests
export const sqlCount = async (table: string): Promise<Result<number, FusionError>> => {
	let r = await sqlQuery<{rowCount: number}>(`select count(*) as rowCount from ${table}`)
	if(r.isErr()) {
		return err(serializeFusionError('sqlCount', r.error))
	}
	const [{rowCount}] = r.value
	return ok(rowCount)
}